import { useEffect, useState } from "react";
import { Sparkles, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * BrutNetScoreBadge — Affiche un score ponctuel (0-100) en mode Brute/Net.
 *
 * V23.15.18 — Réplique légère de la doctrine Brute/Net pour les scores
 * ponctuels (Bras Armé, Fractale, Générateur). Récupère le facteur global
 * de friction de l'org (1 seul appel HTTP partagé par tous les badges
 * d'une même page via cache mémoire ci-dessous).
 *
 * V23.15.32 — Doctrine 3Q : "Et alors ?" — chaque facteur affiché est
 * désormais accompagné de sa traduction CHF/an évitables (tooltip natif).
 *
 * Usage :
 *   <BrutNetScoreBadge brute={c.score_compatibility} />
 *
 * Mode additif strict : le score brute est rendu en premier, le score net
 * est ajouté en dessous avec son explication.
 */

// Cache module-level (1 fetch par page-load) — factor + friction translation
let _factorCache = { value: null, ts: 0 };
let _translationCache = { value: null, ts: 0 };
const FACTOR_CACHE_TTL_MS = 60_000;

export default function BrutNetScoreBadge({ brute = 0, suffix = "", testId = "score-brutnet" }) {
  const [factor, setFactor] = useState(() =>
    _factorCache.value && Date.now() - _factorCache.ts < FACTOR_CACHE_TTL_MS
      ? _factorCache.value
      : null
  );
  const [translation, setTranslation] = useState(() =>
    _translationCache.value && Date.now() - _translationCache.ts < FACTOR_CACHE_TTL_MS
      ? _translationCache.value
      : null
  );

  useEffect(() => {
    let cancelled = false;
    if (!factor) {
      api
        .get("/workspace/valuations/global-friction-factor")
        .then((r) => {
          if (cancelled) return;
          _factorCache = { value: r.data, ts: Date.now() };
          setFactor(r.data);
        })
        .catch(() => {});
    }
    if (!translation) {
      // V23.15.32 — Traduction CHF/an évitables (doctrine 3Q · "Et alors ?")
      api
        .get("/workspace/valuations/friction-translation?currency=CHF")
        .then((r) => {
          if (cancelled) return;
          _translationCache = { value: r.data, ts: Date.now() };
          setTranslation(r.data);
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [factor, translation]);

  const bruteNum = Number(brute || 0);
  const f = factor?.factor ?? 1.0;
  const net = Math.round(bruteNum * f);
  const hasReductions = (factor?.reductions || []).length > 0;
  const reductionsCount = (factor?.reductions || []).length;

  const colorBrute = bruteNum >= 75 ? "#1D9E75" : bruteNum >= 50 ? "#EF9F27" : "#71717A";
  const colorNet = net >= 75 ? "#1D9E75" : net >= 50 ? "#EF9F27" : "#71717A";

  // V23.15.32 — Tooltip enrichi doctrine 3Q
  const factorTooltip = translation?.narrative || (
    hasReductions
      ? `${reductionsCount} friction(s) appliquée(s) : ${(factor.reductions || []).map((r) => `${r.label} ×${r.factor}`).join(" · ")}`
      : ""
  );

  return (
    <div className="text-right" data-testid={testId} title={factorTooltip}>
      <div className="text-2xl font-bold" style={{ color: colorBrute }}>
        {bruteNum}{suffix}
      </div>
      {hasReductions && (
        <div className="mt-1 text-[10px]" data-testid={`${testId}-net`}>
          <div className="inline-flex items-center gap-1 text-zinc-500">
            <Sparkles className="w-2.5 h-2.5 text-[#EF9F27]" />
            <span>Net&nbsp;:</span>
            <span className="font-medium" style={{ color: colorNet }}>{net}</span>
            <span className="text-zinc-600">×{f.toFixed(2)}</span>
          </div>
          {/* V23.15.32 — Traduction CHF/an évitables directement sous le facteur */}
          {translation && translation.pertes_evitables_meur > 0 && (
            <div
              className="inline-flex items-center gap-1 mt-0.5 text-[#EF9F27]"
              data-testid={`${testId}-loss`}
            >
              <AlertTriangle className="w-2.5 h-2.5" />
              <span className="font-medium">≈ {translation.pertes_evitables_display}/an évitables</span>
            </div>
          )}
          <div
            className="text-[9px] text-zinc-600 mt-0.5"
            title={(factor.reductions || []).map((r) => `${r.label} ×${r.factor}`).join(" · ")}
          >
            {reductionsCount} friction(s) appliquée(s)
          </div>
        </div>
      )}
    </div>
  );
}
